import { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import {
  ArrowDownRight,
  ArrowUpRight,
  CalendarRange,
  FolderPlus,
  MoreHorizontal,
  Pencil,
  RefreshCw,
  Trash2,
} from "@digital-twins/design-system/icons";
import {
  SOCIAL_SMALL_CELL_THRESHOLD,
  SocialSentimentCounts,
  SocialSyncSchedule,
  SocialUniverseChannel,
  SocialUniverseItem,
} from "@digital-twins/shared-types";
import Link from "@/routing/Link";
import { AddToCollectionMenu } from "@/components/library/AddToCollectionMenu";
import { CoverChip } from "@/components/library/CollectionItemCard";
import { TopicCover } from "@/components/library/CollectionTopicCard";
import { LIBRARY_CARD_COVER_SCRIM_CLASS } from "@/components/library/libraryCardStyles";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { CountryFlag } from "@/components/ui/CountryFlag";
import { Button } from "@/components/ui/button";
import { Heading } from "@/components/ui/Heading";
import { Skeleton } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/Text";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SocialPlatformIcon } from "./SocialPlatformIcon";
import { SocialReasonTooltip } from "./SocialReasonTooltip";
import { isChannelCreditPaused, socialUpdateNowState } from "./socialUpdateNow";
import { useNow } from "./useNow";
import { formatTopicStat, SocialSparkline } from "./SocialStatRow";
import {
  askAboutTopicHref,
  effectiveSocialCadence,
  formatSocialDateTime,
  formatSocialLanguages,
  SOCIAL_PLATFORM_LABELS,
} from "./socialFormat";
import {
  isChannelInProgress,
  isRunPhaseSorting,
  socialChannelUpdatedAt,
  socialUniverseUpdatedAt,
  universeChannelsNeedingRetry,
} from "./socialTrackerStatus";
import { useSocialItemImage } from "./useSocialItemImage";
import { useSocialUniversePosts } from "./useSocialUniversePosts";
import { socialTopicSeriesQuery, useSocialTopicSeries } from "./useSocialTopicSeries";
import { marketFlagCode } from "../libraryHelpers";

interface SocialUniverseCardProps {
  universe: SocialUniverseItem;
  canManage: boolean;
  isBusy: boolean;
  onOpen: (token: string) => void;
  onSync: (universe: SocialUniverseItem) => void;
  onDelete: (universe: SocialUniverseItem) => void;
  /** Absent where Topics are not edited (the back office). */
  onEdit?: (universe: SocialUniverseItem) => void;
  canAsk?: boolean;
  /** Manage actions as buttons on the card instead of behind the ⋯ menu. */
  showActionsInline?: boolean;
  /** 'manual' where no cron runs: an update then only runs when asked. */
  syncSchedule?: SocialSyncSchedule;
  /** Shows "Add to collection" on the cover. False outside the main app. */
  canAddToCollection?: boolean;
}

/** What the card says about a Topic's posts, from its channels and counts. */
export type SocialUniverseCardState =
  | "collecting"
  | "sorting"
  | "empty"
  | "waiting"
  | "posts";

export function socialUniverseCardState(
  universe: Pick<
    SocialUniverseItem,
    "channels" | "postsPending" | "totalPosts"
  >,
): SocialUniverseCardState {
  if (
    universe.channels.some((channel) => isChannelInProgress(channel))
  ) {
    return "collecting";
  }
  // Sorting follows the runs' phases only: posts left unjudged by a run that
  // closed (a Jev outage) are retried later and do not keep the Topic busy.
  if (universe.channels.some((channel) => isRunPhaseSorting(channel.phase))) {
    return "sorting";
  }
  if (universe.totalPosts > 0) {
    return "posts";
  }
  const hasFinishedFirstUpdate = universe.channels.some(
    (channel) => socialChannelUpdatedAt(channel) != null,
  );
  return hasFinishedFirstUpdate ? "empty" : "waiting";
}

/**
 * The sorting line: the count while posts wait, a count-free line otherwise
 * (the digest step runs with nothing left to sort).
 */
export function socialUniverseSortingLabel(
  t: TFunction<"app">,
  postsPending: number,
): string {
  return postsPending > 0
    ? t("socialListening.topicCard.sorting", { count: postsPending })
    : t("socialListening.topicActivity.sorting");
}

const SENTIMENT_SHARE_KEYS: Array<keyof SocialSentimentCounts> = [
  "positive",
  "neutral",
  "negative",
  "mixed",
];

/**
 * Share of each sentiment among the posts that have one, in whole percents
 * that always sum to 100 (largest remainder: floor each share, then give the
 * points left over to the largest fractions, ties in sentiment order); null
 * below SOCIAL_SMALL_CELL_THRESHOLD posts, where a share reads as more than it is.
 */
export function socialSentimentShares(
  counts: SocialSentimentCounts,
): Record<keyof SocialSentimentCounts, number> | null {
  const total =
    counts.positive + counts.neutral + counts.negative + counts.mixed;
  if (total < SOCIAL_SMALL_CELL_THRESHOLD) {
    return null;
  }
  // Integer math, so equal fractions tie exactly.
  const scaled = SENTIMENT_SHARE_KEYS.map((key) => counts[key] * 100);
  const shares = scaled.map((value) => Math.floor(value / total));
  const remainders = scaled.map((value) => value % total);
  let leftover = 100 - shares.reduce((sum, value) => sum + value, 0);
  const byRemainder = SENTIMENT_SHARE_KEYS.map((_, index) => index).sort(
    (a, b) => remainders[b] - remainders[a] || a - b,
  );
  for (const index of byRemainder) {
    if (leftover <= 0) {
      break;
    }
    shares[index] += 1;
    leftover -= 1;
  }
  return {
    positive: shares[0],
    neutral: shares[1],
    negative: shares[2],
    mixed: shares[3],
  };
}

interface SocialUniversePreviewImageProps {
  imagePath: string;
  alt: string;
  testId: string;
}

/** One post image on the card's cover strip; a token tint while loading or when it fails. */
function SocialUniversePreviewImage(props: SocialUniversePreviewImageProps) {
  const { imagePath, alt, testId } = props;

  const { src, isLoading } = useSocialItemImage(imagePath);

  if (src) {
    return (
      <img
        src={src}
        alt={alt}
        decoding="async"
        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04] motion-reduce:transition-none"
        data-testid={testId}
      />
    );
  }
  if (isLoading) {
    return <Skeleton className="h-full w-full rounded-none" />;
  }
  return (
    <span
      className="block h-full w-full bg-primary/10"
      aria-hidden="true"
      data-testid={testId}
    />
  );
}

/** The channel with the most listed posts, when there are posts at all. */
export function socialUniverseTopChannel(
  channels: Array<SocialUniverseChannel>,
): SocialUniverseChannel | null {
  const ranked = channels
    .filter((channel) => channel.totalPosts > 0)
    .sort((a, b) => b.totalPosts - a.totalPosts);
  return ranked[0] ?? null;
}

/** Up, down or flat: posts in the last 7 days against the 7 days before. */
export function socialUniverseWeekTrend(
  universe: Pick<SocialUniverseItem, "postsLast7Days" | "postsPrevious7Days">,
): { count: number; direction: "up" | "down" | "flat" } | null {
  const { postsLast7Days, postsPrevious7Days } = universe;
  if (postsLast7Days == null || postsPrevious7Days == null) {
    return null;
  }
  if (postsLast7Days === 0 && postsPrevious7Days === 0) {
    return null;
  }
  const direction =
    postsLast7Days > postsPrevious7Days
      ? "up"
      : postsLast7Days < postsPrevious7Days
        ? "down"
        : "flat";
  return { count: postsLast7Days, direction };
}

/** The round icon buttons on the cover: the ⋯ menu and "Add to collection". */
const COVER_ICON_BUTTON_CLASS =
  "h-8 w-8 rounded-full bg-media-scrim/60 text-media-scrim-foreground backdrop-blur-md hover:bg-media-scrim/80 hover:text-media-scrim-foreground";

const SENTIMENT_BAR_ORDER: Array<keyof SocialSentimentCounts> = [
  "negative",
  "mixed",
  "neutral",
  "positive",
];

const SENTIMENT_BAR_CLASS: Record<keyof SocialSentimentCounts, string> = {
  negative: "bg-chart-negative",
  mixed: "bg-foreground/45",
  neutral: "bg-foreground/20",
  positive: "bg-chart-positive",
};

interface CardStatProps {
  label: string;
  value: string;
  testId: string;
  note?: ReactNode;
  /** A small trend under the figure. */
  spark?: ReactNode;
}

function CardStat(props: CardStatProps) {
  const { label, value, testId, note, spark } = props;

  return (
    <div className="min-w-0" data-testid={testId}>
      <dt className="text-[12px] text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 flex items-baseline gap-1.5 whitespace-nowrap">
        <span className="text-[19px] font-semibold leading-tight tracking-[-0.02em] tabular-nums text-foreground">
          {value}
        </span>
        {note}
      </dd>
      {spark}
    </div>
  );
}

export function SocialUniverseCard(props: SocialUniverseCardProps) {
  const {
    universe,
    canManage,
    isBusy,
    onOpen,
    onSync,
    onDelete,
    onEdit,
    canAsk = true,
    showActionsInline = false,
    syncSchedule = "daily",
    canAddToCollection = false,
  } = props;

  const { t, i18n } = useTranslation("app");

  const testId = `social-universe-card-${universe.token}`;
  const state = socialUniverseCardState(universe);
  const shares = socialSentimentShares(universe.sentiment);
  const lastSynced = formatSocialDateTime(
    socialUniverseUpdatedAt(universe),
    i18n.language,
  );
  const troubleChannels = universeChannelsNeedingRetry(universe.channels);
  const isPaused =
    universe.channels.length > 0 &&
    universe.channels.every((channel) => channel.status === "paused");
  // Any channel paused for credits (S5).
  const isCreditPaused = universe.channels.some(isChannelCreditPaused);
  const now = useNow(universe.manualUpdateAvailableAt);
  const updateNow = socialUpdateNowState({
    t,
    lng: i18n.language,
    now,
    isCreditPaused,
    availableAt: universe.manualUpdateAvailableAt,
    maxCreditsPerUpdate: universe.maxCreditsPerUpdate,
  });
  const isUpdating = state === "collecting";
  const canEdit = canManage && onEdit != null;
  const askThisWeekHref =
    canAsk && universe.totalPosts > 0
      ? askAboutTopicHref(
          { topicToken: universe.token, window: "7d" },
          {
            question: t("socialListening.topicAsk.thisWeek", {
              name: universe.name,
            }),
          },
        )
      : null;
  const previewPaths = universe.previewImagePaths.slice(0, 4);
  const platforms = universe.channels.map((channel) => channel.platform);
  const cadence = effectiveSocialCadence(universe.cadence, syncSchedule);
  const hasPosts = universe.totalPosts > 0;
  const trend = socialUniverseWeekTrend(universe);
  const topChannel = socialUniverseTopChannel(universe.channels);
  const reach = universe.reach ?? null;
  const creators = universe.creators ?? null;
  const format = (value: number) => formatTopicStat(value, i18n.language);
  // No Topic previews: the top posts by reach lend their stored stills to the
  // cover. Raw platform CDN links are never shown (they expire and 403).
  const { posts: topPosts } = useSocialUniversePosts(
    hasPosts && previewPaths.length === 0 ? universe.token : null,
    { sort: "views", limit: 3, refreshKey: universe.lastSyncedAt },
  );
  const topPostPaths = topPosts
    .map((post) => post.images?.[0]?.imagePath ?? null)
    .filter((path): path is string => Boolean(path));
  const coverPaths = previewPaths.length > 0 ? previewPaths : topPostPaths;
  const seriesQuery = useMemo(() => socialTopicSeriesQuery(universe, {}), [universe]);
  const { postBuckets } = useSocialTopicSeries(
    hasPosts ? universe.token : null,
    hasPosts ? seriesQuery : null,
    universe.lastSyncedAt,
  );
  const reachSpark = postBuckets.map((entry) => entry.total.views);

  const pulse = (
    <span
      className="mr-1.5 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-primary align-middle"
      aria-hidden="true"
    />
  );

  const statusLine =
    state === "collecting" ? (
      <Text
        size="sm"
        className="text-[13px]"
        data-testid={`${testId}-collecting`}
      >
        {pulse}
        {t("socialListening.topicCard.collecting", {
          count: universe.totalPosts,
        })}
      </Text>
    ) : state === "sorting" ? (
      <Text size="sm" className="text-[13px]" data-testid={`${testId}-sorting`}>
        {pulse}
        {socialUniverseSortingLabel(t, universe.postsPending)}
      </Text>
    ) : state === "empty" ? (
      <Text
        size="sm"
        color="muted"
        className="text-[13px]"
        data-testid={`${testId}-empty`}
      >
        {t("socialListening.topicCard.emptyTitle")}
      </Text>
    ) : state === "waiting" ? (
      <Text
        size="sm"
        color="muted"
        className="text-[13px]"
        data-testid={`${testId}-waiting`}
      >
        {t("socialListening.topicCard.notUpdatedYet")}
      </Text>
    ) : null;

  const trendNote = trend ? (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 text-[12px] font-medium tabular-nums",
        trend.direction === "up" && "text-chart-positive",
        trend.direction === "down" && "text-chart-negative",
        trend.direction === "flat" && "text-muted-foreground",
      )}
      data-direction={trend.direction}
      data-testid={`${testId}-trend`}
    >
      {trend.direction === "down" ? (
        <ArrowDownRight className="h-3 w-3" aria-hidden="true" />
      ) : trend.direction === "up" ? (
        <ArrowUpRight className="h-3 w-3" aria-hidden="true" />
      ) : null}
      {t("socialListening.topicCard.stats.thisWeek", {
        count: trend.count,
        value: format(trend.count),
      })}
    </span>
  ) : null;

  const stats = hasPosts ? (
    <div className="space-y-2.5" data-testid={`${testId}-summary`}>
      <dl
        className="grid grid-cols-3 gap-3 px-0.5 py-1"
        data-testid={`${testId}-stats`}
      >
        <CardStat
          label={t("socialListening.topicCard.stats.posts")}
          value={format(universe.totalPosts)}
          testId={`${testId}-stat-posts`}
        />
        <CardStat
          label={t("socialListening.topicCard.stats.reach")}
          value={
            reach != null
              ? format(reach)
              : t("socialListening.topicPage.stats.notAvailable")
          }
          testId={`${testId}-stat-reach`}
          spark={<SocialSparkline values={reachSpark} testId={`${testId}-reach-spark`} />}
        />
        <CardStat
          label={t("socialListening.topicCard.stats.creators")}
          value={
            creators != null
              ? format(creators)
              : t("socialListening.topicPage.stats.notAvailable")
          }
          testId={`${testId}-stat-creators`}
        />
      </dl>
      {trendNote || topChannel ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-muted-foreground">
          {trendNote}
          {topChannel ? (
            <span
              className="inline-flex items-center gap-1"
              data-testid={`${testId}-top-channel`}
            >
              <SocialPlatformIcon
                platform={topChannel.platform}
                className="h-3 w-3"
              />
              {t("socialListening.topicCard.stats.topChannel", {
                channel: SOCIAL_PLATFORM_LABELS[topChannel.platform],
              })}
            </span>
          ) : null}
        </div>
      ) : null}
      {shares ? (
        <div className="space-y-1" data-testid={`${testId}-sentiment`}>
          <div
            className="flex h-1.5 w-full overflow-hidden rounded-full bg-muted"
            role="img"
            aria-label={t("socialListening.sentimentShare.positive", {
              percent: shares.positive,
            })}
          >
            {SENTIMENT_BAR_ORDER.map((key) =>
              shares[key] > 0 ? (
                <span
                  key={key}
                  className={SENTIMENT_BAR_CLASS[key]}
                  style={{ width: `${shares[key]}%` }}
                />
              ) : null,
            )}
          </div>
          <span className="text-[12px] text-muted-foreground tabular-nums">
            {t("socialListening.sentimentShare.positive", {
              percent: shares.positive,
            })}
          </span>
        </div>
      ) : null}
    </div>
  ) : null;

  const menuItems = (
    <>
      {askThisWeekHref ? (
        <DropdownMenuItem asChild>
          <Link href={askThisWeekHref} data-testid={`${testId}-ask-week`}>
            <CalendarRange className="mr-2 h-4 w-4" />
            {t("socialListening.card.askThisWeek")}
          </Link>
        </DropdownMenuItem>
      ) : null}
      {canManage ? (
        <>
          {askThisWeekHref ? <DropdownMenuSeparator /> : null}
          <DropdownMenuItem
            disabled={isBusy || isUpdating || isPaused || updateNow.isBlocked}
            data-testid={`${testId}-sync`}
            onClick={() => onSync(universe)}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            {t("socialListening.topicCard.updateNow")}
            {updateNow.shortReason ? (
              <span
                className="ml-auto pl-3 text-xs text-muted-foreground"
                data-testid={`${testId}-sync-reason`}
              >
                {updateNow.shortReason}
              </span>
            ) : null}
          </DropdownMenuItem>
          {canEdit ? (
            <DropdownMenuItem
              disabled={isBusy}
              data-testid={`${testId}-edit`}
              onClick={() => onEdit?.(universe)}
            >
              <Pencil className="mr-2 h-4 w-4" />
              {t("socialListening.topicCard.editTopic")}
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            disabled={isBusy}
            className="text-destructive focus:text-destructive"
            data-testid={`${testId}-delete`}
            onClick={() => onDelete(universe)}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {t("socialListening.topicCard.delete")}
          </DropdownMenuItem>
        </>
      ) : null}
    </>
  );

  const inlineActions = canManage ? (
    <div className="relative z-[2] flex flex-wrap items-center gap-2">
      <SocialReasonTooltip
        reason={updateNow.reason}
        isDisabled={isBusy || isUpdating || isPaused || updateNow.isBlocked}
        testId={`${testId}-sync`}
      >
        <Button
          type="button"
          variant="outline"
          size="sm"
          shape="pill"
          className="h-8 px-3.5"
          disabled={isBusy || isUpdating || isPaused || updateNow.isBlocked}
          data-testid={`${testId}-sync`}
          onClick={() => onSync(universe)}
        >
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
          {t("socialListening.topicCard.updateNow")}
        </Button>
      </SocialReasonTooltip>
      {canEdit ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          shape="pill"
          className="h-8 px-3.5"
          disabled={isBusy}
          data-testid={`${testId}-edit`}
          onClick={() => onEdit?.(universe)}
        >
          <Pencil className="mr-1.5 h-3.5 w-3.5" />
          {t("socialListening.topicCard.editTopic")}
        </Button>
      ) : null}
      <Button
        type="button"
        variant="outline"
        size="sm"
        shape="pill"
        className="h-8 px-3.5 text-destructive hover:text-destructive"
        disabled={isBusy}
        data-testid={`${testId}-delete`}
        onClick={() => onDelete(universe)}
      >
        <Trash2 className="mr-1.5 h-3.5 w-3.5" />
        {t("socialListening.topicCard.delete")}
      </Button>
    </div>
  ) : null;

  const menu =
    !showActionsInline && (askThisWeekHref || canManage) ? (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={COVER_ICON_BUTTON_CLASS}
            aria-label={t("socialListening.topicCard.menuAria", {
              name: universe.name,
            })}
            data-testid={`${testId}-menu`}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[200px] rounded-xl">
          {menuItems}
        </DropdownMenuContent>
      </DropdownMenu>
    ) : null;

  const addToCollection = canAddToCollection ? (
    <AddToCollectionMenu
      items={[{ kind: "topic", token: universe.token }]}
      testId={`${testId}-add-menu`}
      trigger={
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={COVER_ICON_BUTTON_CLASS}
          aria-label={t("library.collections.addToCollection")}
          data-testid={`${testId}-add-to-collection`}
        >
          <FolderPlus className="h-4 w-4" />
        </Button>
      }
    />
  ) : null;

  const coverActions =
    addToCollection || menu ? (
      <div className="absolute right-2.5 top-2.5 z-[2] flex items-center gap-1.5">
        {addToCollection}
        {menu}
      </div>
    ) : null;

  const cover = (
    <div className="relative h-36 w-full overflow-hidden bg-muted">
      {coverPaths.length > 0 ? (
        <div
          className="grid h-full w-full gap-0.5"
          style={{
            gridTemplateColumns: `repeat(${coverPaths.length}, minmax(0, 1fr))`,
          }}
          data-testid={`${testId}-previews`}
        >
          {coverPaths.map((imagePath, index) => (
            <SocialUniversePreviewImage
              key={imagePath}
              imagePath={imagePath}
              alt={t("socialListening.topicCard.previewAlt", {
                name: universe.name,
              })}
              testId={`${testId}-preview-${index}`}
            />
          ))}
        </div>
      ) : (
        <TopicCover
          platforms={platforms}
          testId={`${testId}-cover`}
          isFeatured={false}
        />
      )}
      <span className={LIBRARY_CARD_COVER_SCRIM_CLASS} aria-hidden="true" />
      <div className="absolute bottom-2.5 left-2.5 right-2.5 flex min-w-0 items-center gap-1.5">
        {platforms.length > 0 ? (
          <CoverChip testId={`${testId}-channels`} className="gap-1.5">
            {universe.channels.map((channel) => (
              <span
                key={channel.trackerToken}
                className="inline-flex items-center gap-1"
              >
                <SocialPlatformIcon
                  platform={channel.platform}
                  className="h-3.5 w-3.5"
                />
                {universe.channels.length <= 2
                  ? SOCIAL_PLATFORM_LABELS[channel.platform]
                  : (
                    <span className="sr-only">
                      {SOCIAL_PLATFORM_LABELS[channel.platform]}
                    </span>
                  )}
              </span>
            ))}
          </CoverChip>
        ) : null}
        {isCreditPaused ? (
          <CoverChip testId={`${testId}-paused-credits`}>
            <span
              aria-hidden="true"
              className="h-1.5 w-1.5 shrink-0 rounded-full bg-chart-warning"
            />
            {t("socialListening.credits.card.paused")}
          </CoverChip>
        ) : isPaused ? (
          <CoverChip testId={`${testId}-paused`}>
            {t("socialListening.topicCard.paused")}
          </CoverChip>
        ) : null}
      </div>
      {coverActions}
    </div>
  );

  return (
    <article
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-card-border bg-background transition duration-200 hover:-translate-y-0.5 hover:border-foreground/20 hover:shadow-lg motion-reduce:transform-none motion-reduce:transition-none"
      data-testid={testId}
      data-state={state}
    >
      <button
        type="button"
        aria-label={t("socialListening.topicCard.openAria", {
          name: universe.name,
        })}
        className="absolute inset-0 z-[1] rounded-[inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        data-testid={`${testId}-open`}
        onClick={() => onOpen(universe.token)}
      />
      {cover}
      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="min-w-0 space-y-1">
          <Heading
            level={3}
            className="line-clamp-1 font-display text-[20px] font-bold [:root:not([data-heading-font])_&]:font-normal leading-snug tracking-[-0.005em]"
            data-testid={`${testId}-title`}
          >
            {universe.name}
          </Heading>
          <Text
            size="sm"
            color="muted"
            className="flex min-w-0 items-center gap-1.5 text-[13px]"
            data-testid={`${testId}-market`}
          >
            {universe.market ? (
              <CountryFlag
                code={marketFlagCode(universe.market)}
                data-testid={`${testId}-market-flag`}
              />
            ) : null}
            <span className="truncate">
              {[universe.market, formatSocialLanguages(universe.languages, i18n.language)]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </Text>
        </div>

        {stats}
        {statusLine}

        <div className="mt-auto flex flex-wrap items-center gap-x-2 gap-y-1.5 pt-0.5 text-[12px] text-muted-foreground">
          <span data-testid={`${testId}-updated`}>
            {lastSynced
              ? t("socialListening.topicCard.updated", { date: lastSynced })
              : t("socialListening.topicCard.notUpdatedYet")}
          </span>
          <span aria-hidden="true">·</span>
          <span data-testid={`${testId}-cadence`}>
            {t(`socialListening.card.cadence.${cadence}`)}
          </span>
          {troubleChannels.length > 0 ? (
            <Badge
              variant="warning"
              className="rounded-full px-2 py-0 text-[12px]"
              data-testid={`${testId}-trouble`}
            >
              {t("socialListening.topicCard.channelTrouble", {
                count: troubleChannels.length,
              })}
            </Badge>
          ) : null}
        </div>

        {showActionsInline ? inlineActions : null}
      </div>
    </article>
  );
}
